/**
 * 그룹 편집 전용 페이지 — /admin/roles/:id.
 *
 * 5 주요 섹션 (세로 배치, 섹션별 [저장]):
 *   1. 기본 정보  — 이름·설명·활성
 *   2. Skill      — role_skill_grants
 *   3. 자동 부여 룰 — role_attribute_grants
 *   4. 수동 멤버   — user_role_assignments
 *   5. 제외 사용자 — user_role_exclusions (목록 + 복구)
 *
 * 모달에서 페이지로 전환 이유: 1000명 멤버 / 100+ 룰 폭증 시 모달은 좁음.
 * 사용자 편집 페이지 (`/admin/users/:id`) 와 동일한 패턴.
 */
import { Modal } from '@/components/admin/Modal';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { RbacMatchMode, RbacRole, Skill, UserAttributeDefinition } from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Trash2, Users } from 'lucide-react';
import { memo, useEffect, useMemo, useState } from 'react';
import { AttributeRulesEditor, SkillMultiSelect } from '../Roles';

interface Props {
  roleId: string;
}

export function AdminRoleDetail({ roleId }: Props) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [membersOpen, setMembersOpen] = useState(false);

  const detailQuery = useQuery({
    queryKey: ['admin', 'roles', roleId],
    queryFn: () => api.adminRoleGet(roleId),
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.adminRoleDelete(roleId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'roles'] });
      navigate({ to: '/admin/roles' });
    },
  });

  if (detailQuery.isLoading) {
    return (
      <PageShell header={<BackLink />}>
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      </PageShell>
    );
  }
  if (!detailQuery.data) {
    return (
      <PageShell header={<BackLink />}>
        <p className="text-sm text-destructive">
          {detailQuery.error instanceof ApiError
            ? detailQuery.error.message
            : '그룹을 찾을 수 없습니다.'}
        </p>
      </PageShell>
    );
  }

  const role = detailQuery.data;

  return (
    <PageShell
      header={
        <>
          <BackLink />
          <PageHeader
            title={role.systemKind === 'all_users' ? `${role.name} [전체]` : role.name}
            description={
              role.systemKind === 'all_users'
                ? `${role.enabled ? '활성' : '비활성'} · Skill ${role.skillIds.length}개 · 모든 비-admin 사용자에게 자동 부여`
                : `${role.enabled ? '활성' : '비활성'} · Skill ${role.skillIds.length}개 · 멤버 ${role.memberCount}명`
            }
            actions={
              <div className="flex items-center gap-2">
                <Button variant="ghost" type="button" onClick={() => setMembersOpen(true)}>
                  <Users className="size-4" /> 멤버 보기
                </Button>
                {role.systemKind ? null : (
                  <Button
                    variant="ghost"
                    type="button"
                    onClick={() => {
                      if (
                        window.confirm(
                          `'${role.name}' 그룹을 삭제하시겠습니까? 이 그룹으로 부여된 Skill 권한이 즉시 회수됩니다.`,
                        )
                      ) {
                        deleteMutation.mutate();
                      }
                    }}
                  >
                    <Trash2 className="size-4" /> 삭제
                  </Button>
                )}
              </div>
            }
          />
        </>
      }
    >
      <div className="space-y-5">
        <BasicSection role={role} />
        <SkillsSection role={role} />
        {role.systemKind === 'all_users' ? null : <RulesSection role={role} />}
        {role.systemKind === 'all_users' ? null : <ManualMembersSection role={role} />}
        <ExclusionsSection role={role} />
      </div>
      <MembersOverviewModal open={membersOpen} onClose={() => setMembersOpen(false)} role={role} />
    </PageShell>
  );
}

function BackLink() {
  return (
    <Link
      to="/admin/roles"
      className="mb-4 inline-flex items-center gap-1 text-xs text-foreground-muted hover:text-foreground"
    >
      <ArrowLeft className="size-3.5" /> 그룹 목록
    </Link>
  );
}

// ─────────────────────────── 1. 기본 정보 ───────────────────────────

function BasicSection({ role }: { role: RbacRole }) {
  const qc = useQueryClient();
  const [name, setName] = useState(role.name);
  const [description, setDescription] = useState(role.description ?? '');
  const [enabled, setEnabled] = useState(role.enabled);
  const [error, setError] = useState<string | null>(null);

  const dirty =
    name.trim() !== role.name ||
    (description.trim() || null) !== role.description ||
    enabled !== role.enabled;

  const mutation = useMutation({
    mutationFn: () =>
      api.adminRoleUpdate(role.id, {
        name: name.trim(),
        description: description.trim() || null,
        enabled,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'roles'] });
      qc.invalidateQueries({ queryKey: ['admin', 'roles', role.id] });
    },
    onError: (e: unknown) => setError(e instanceof ApiError ? e.message : '저장에 실패했습니다.'),
  });

  return (
    <Section
      title="기본 정보"
      action={
        dirty ? (
          <Button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || !name.trim()}
          >
            {mutation.isPending ? '저장 중…' : '저장'}
          </Button>
        ) : null
      }
    >
      {error ? (
        <div className="mb-2 rounded-md bg-red-500/10 px-3 py-2 text-sm text-red-300">{error}</div>
      ) : null}
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="이름" htmlFor="role-name">
          <Input id="role-name" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="상태" htmlFor="role-enabled">
          <label className="flex h-10 items-center gap-2 rounded-md border border-border bg-surface px-3 text-sm">
            <input
              id="role-enabled"
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            <span>{enabled ? '활성' : '비활성 — 권한 미적용'}</span>
          </label>
        </Field>
      </div>
      <div className="mt-4">
        <Field label="설명" htmlFor="role-desc">
          <Input
            id="role-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="이 그룹이 무엇을 위한 것인지 (선택)"
          />
        </Field>
      </div>
    </Section>
  );
}

// ─────────────────────────── 2. Skill ───────────────────────────

function SkillsSection({ role }: { role: RbacRole }) {
  const qc = useQueryClient();
  const skillsQuery = useQuery({
    queryKey: ['admin', 'skills'],
    queryFn: api.adminSkillList,
  });
  const [skillIds, setSkillIds] = useState<string[]>(role.skillIds);
  const [error, setError] = useState<string | null>(null);

  const allSkills: Skill[] = (skillsQuery.data?.skills ?? []).filter((s) => s.enabled);
  const dirty = !arrayEq(skillIds, role.skillIds);

  const mutation = useMutation({
    mutationFn: () => api.adminRoleSetSkills(role.id, { skillIds }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'roles'] });
      qc.invalidateQueries({ queryKey: ['admin', 'roles', role.id] });
    },
    onError: (e: unknown) => setError(e instanceof ApiError ? e.message : '저장에 실패했습니다.'),
  });

  return (
    <Section
      title="Skill"
      description={`이 그룹에 부여된 Skill (${skillIds.length}개). 그룹을 가진 모든 사용자에게 노출됩니다.`}
      action={
        dirty ? (
          <Button type="button" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? '저장 중…' : '저장'}
          </Button>
        ) : null
      }
    >
      {error ? (
        <div className="mb-2 rounded-md bg-red-500/10 px-3 py-2 text-sm text-red-300">{error}</div>
      ) : null}
      <SkillMultiSelect
        skills={allSkills}
        value={skillIds}
        onToggle={(id) =>
          setSkillIds((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]))
        }
        isLoading={skillsQuery.isLoading}
      />
    </Section>
  );
}

// ─────────────────────────── 3. 자동 부여 룰 ───────────────────────────

function RulesSection({ role }: { role: RbacRole }) {
  const qc = useQueryClient();
  const attrDefsQuery = useQuery({
    queryKey: ['admin', 'user-attributes'],
    queryFn: api.adminUserAttributesList,
  });
  const rulesQuery = useQuery({
    queryKey: ['admin', 'roles', role.id, 'attribute-grants'],
    queryFn: () => api.adminRoleGetAttributeGrants(role.id),
  });
  const [rules, setRules] = useState<
    Array<{
      attributeKey: string;
      attributeValue: string;
      matchMode: RbacMatchMode;
      negate: boolean;
    }>
  >([]);
  const [rulesInitialized, setRulesInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!rulesInitialized && rulesQuery.data) {
    setRules(rulesQuery.data.rules);
    setRulesInitialized(true);
  }

  const attrDefs: UserAttributeDefinition[] = attrDefsQuery.data?.definitions ?? [];

  const dirty = (() => {
    if (!rulesInitialized) return false;
    const before = rulesQuery.data?.rules ?? [];
    if (before.length !== rules.length) return true;
    return rules.some(
      (r, i) =>
        !before[i] ||
        before[i].attributeKey !== r.attributeKey ||
        before[i].attributeValue !== r.attributeValue ||
        before[i].matchMode !== r.matchMode ||
        before[i].negate !== r.negate,
    );
  })();

  const mutation = useMutation({
    mutationFn: () => api.adminRoleSetAttributeGrants(role.id, { rules }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'roles', role.id, 'attribute-grants'] });
      qc.invalidateQueries({ queryKey: ['admin', 'permissions', 'attribute-rules'] });
    },
    onError: (e: unknown) => setError(e instanceof ApiError ? e.message : '저장에 실패했습니다.'),
  });

  return (
    <Section
      title="자동 부여 룰"
      description="사용자의 attribute 값이 룰과 매칭되면 이 그룹이 자동 부여됩니다. 같은 attribute 의 여러 값은 OR, 다른 attribute 끼리는 AND."
      action={
        dirty ? (
          <Button type="button" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? '저장 중…' : '저장'}
          </Button>
        ) : null
      }
    >
      {error ? (
        <div className="mb-2 rounded-md bg-red-500/10 px-3 py-2 text-sm text-red-300">{error}</div>
      ) : null}
      <AttributeRulesEditor
        defs={attrDefs}
        value={rules}
        onChange={setRules}
        isLoading={attrDefsQuery.isLoading || rulesQuery.isLoading}
      />
      <RulePreviewInline roleId={role.id} rules={rules} />
    </Section>
  );
}

// ─────────────────────────── 3.1 룰 미리보기 (인라인) ───────────────────────────

/**
 * 자동 부여 룰 인라인 미리보기 — 편집기 state 변경 시 debounce 후 서버 호출.
 * 카운트 + 샘플 5명만 노출. 전체 리스트 / 다른 부여 경로는 상단 [멤버 보기] 모달.
 */
function RulePreviewInline({
  roleId,
  rules,
}: {
  roleId: string;
  rules: Array<{
    attributeKey: string;
    attributeValue: string;
    matchMode: RbacMatchMode;
    negate: boolean;
  }>;
}) {
  // 룰 state 를 string 으로 키화 → debounce 입력 트리거.
  const rulesKey = useMemo(
    () =>
      rules
        .map((r) => `${r.attributeKey}|${r.attributeValue}|${r.matchMode}|${r.negate}`)
        .sort()
        .join('||'),
    [rules],
  );
  const [debouncedKey, setDebouncedKey] = useState(rulesKey);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedKey(rulesKey), 400);
    return () => clearTimeout(t);
  }, [rulesKey]);

  const previewQuery = useQuery({
    queryKey: ['admin', 'roles', roleId, 'preview-members', { rulesKey: debouncedKey }],
    queryFn: () => api.adminRolePreviewMembers(roleId, { rules }),
    enabled: rules.length > 0,
  });

  if (rules.length === 0) return null;

  const total = previewQuery.data?.totalCount ?? 0;
  const sample = previewQuery.data?.members.slice(0, 5) ?? [];

  return (
    <div className="mt-3 rounded-md border border-border bg-surface-muted/40 px-3 py-2">
      <div className="text-xs text-foreground-muted">
        현재 룰로 매칭:{' '}
        {previewQuery.isLoading ? (
          <span>계산 중…</span>
        ) : previewQuery.error ? (
          <span className="text-destructive">
            {previewQuery.error instanceof ApiError ? previewQuery.error.message : '오류'}
          </span>
        ) : (
          <span className="text-foreground font-medium">{total}명</span>
        )}
      </div>
      {sample.length > 0 ? (
        <div className="mt-1.5 flex flex-wrap gap-1.5 text-xs">
          {sample.map((m) => (
            <span key={m.id} className="rounded bg-surface px-1.5 py-0.5">
              {m.displayName}
              <span className="ml-1 text-foreground-muted">({m.email})</span>
            </span>
          ))}
          {total > sample.length ? (
            <span className="text-foreground-muted">외 {total - sample.length}명</span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ─────────────────────────── 4. 수동 멤버 ───────────────────────────

/** 좌/우 transfer list 한 줄. */
interface UserRow {
  id: string;
  displayName: string;
  email: string;
  disabledAt: string | null;
}

function ManualMembersSection({ role }: { role: RbacRole }) {
  const qc = useQueryClient();
  // 검색 입력 → debounce 후 서버 호출.
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchDraft.trim()), 200);
    return () => clearTimeout(t);
  }, [searchDraft]);

  // 검색어가 있으면 서버 검색, 비어있으면 list 기본 목록.
  const usersQuery = useQuery({
    queryKey: ['admin', 'users', { search }],
    queryFn: () => api.adminUsersList(search ? { search } : undefined),
  });

  // 이 그룹의 현재 멤버 상세 정보 — 멤버 표시 / 해제 가능하게 한다.
  const membersQuery = useQuery({
    queryKey: ['admin', 'roles', role.id, 'members-detail'],
    queryFn: () => api.adminRoleGetMembers(role.id),
  });

  const [memberIds, setMemberIds] = useState<string[]>(role.memberIds);
  const [error, setError] = useState<string | null>(null);

  // 사용자 정보 lookup (검색 결과 ∪ 기존 멤버 정보) — 좌/우 패널 둘 다 displayName/email 표시용.
  const userInfoById = useMemo(() => {
    const map = new Map<string, UserRow>();
    for (const u of usersQuery.data?.users ?? []) {
      if (u.role !== 'user') continue;
      map.set(u.id, {
        id: u.id,
        displayName: u.displayName,
        email: u.email,
        disabledAt: u.disabledAt,
      });
    }
    for (const m of membersQuery.data?.members ?? []) {
      if (!map.has(m.id)) map.set(m.id, m);
    }
    return map;
  }, [usersQuery.data, membersQuery.data]);

  // 좌측 후보 — 검색 결과 중 아직 memberIds 에 없는 사용자.
  const leftCandidates: UserRow[] = useMemo(() => {
    const memberSet = new Set(memberIds);
    return Array.from(userInfoById.values())
      .filter((u) => !memberSet.has(u.id))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [userInfoById, memberIds]);

  // 우측 할당 — memberIds 에 있는 모든 사용자 (검색과 무관, 모두 표시).
  const rightMembers: UserRow[] = useMemo(() => {
    const rows: UserRow[] = [];
    for (const id of memberIds) {
      const u = userInfoById.get(id);
      if (u) rows.push(u);
    }
    return rows.sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [userInfoById, memberIds]);

  const dirty = !arrayEq(memberIds, role.memberIds);

  const mutation = useMutation({
    mutationFn: () => api.adminRoleSetMembers(role.id, { userIds: memberIds }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'roles'] });
      qc.invalidateQueries({ queryKey: ['admin', 'roles', role.id] });
      qc.invalidateQueries({ queryKey: ['admin', 'roles', role.id, 'members-detail'] });
    },
    onError: (e: unknown) => setError(e instanceof ApiError ? e.message : '저장에 실패했습니다.'),
  });

  const isLoading = usersQuery.isLoading || membersQuery.isLoading;

  return (
    <Section
      title="수동 할당 멤버"
      description={`부서·룰과 무관하게 명시적으로 이 그룹에 합류시킨 사용자 (${memberIds.length}명). 룰로 자동 부여된 사용자는 여기 표시 안 됨. 후보를 클릭하면 → 할당으로 이동, 할당을 클릭하면 ← 후보로 이동.`}
      action={
        dirty ? (
          <Button type="button" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? '저장 중…' : '저장'}
          </Button>
        ) : null
      }
    >
      {error ? (
        <div className="mb-2 rounded-md bg-red-500/10 px-3 py-2 text-sm text-red-300">{error}</div>
      ) : null}
      <div className="space-y-3">
        <Input
          placeholder="이메일 / 이름 검색 (후보 필터)"
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
        />
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <TransferListPane
            title="후보"
            count={leftCandidates.length}
            users={leftCandidates}
            emptyHint={
              isLoading ? '불러오는 중…' : search ? '검색 결과 없음.' : '모든 사용자가 이미 할당됨.'
            }
            actionLabel="→"
            actionTone="text-emerald-300"
            onItemClick={(id) => setMemberIds((prev) => [...prev, id])}
          />
          <TransferListPane
            title="할당"
            count={rightMembers.length}
            users={rightMembers}
            emptyHint={isLoading ? '불러오는 중…' : '아직 할당된 멤버가 없습니다.'}
            actionLabel="←"
            actionTone="text-red-300"
            onItemClick={(id) => setMemberIds((prev) => prev.filter((x) => x !== id))}
          />
        </div>
      </div>
    </Section>
  );
}

/** 좌/우 패널 1개 — 클릭 시 onItemClick(userId). */
function TransferListPane({
  title,
  count,
  users,
  emptyHint,
  actionLabel,
  actionTone,
  onItemClick,
}: {
  title: string;
  count: number;
  users: UserRow[];
  emptyHint: string;
  actionLabel: string;
  actionTone: string;
  onItemClick: (id: string) => void;
}) {
  return (
    <div className="flex flex-col rounded-md border border-border bg-surface">
      <header className="border-b border-border px-3 py-2 text-xs text-foreground-muted">
        <span className="font-medium text-foreground">{title}</span>
        <span className="ml-1">({count})</span>
      </header>
      <div className="max-h-[480px] min-h-[280px] overflow-y-auto p-1.5">
        {users.length === 0 ? (
          <div className="px-2 py-6 text-center text-xs text-foreground-muted">{emptyHint}</div>
        ) : (
          <ul className="space-y-0.5">
            {users.map((u) => (
              <li key={u.id}>
                <button
                  type="button"
                  onClick={() => onItemClick(u.id)}
                  className="group flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-surface-muted"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium">{u.displayName}</span>
                      {u.disabledAt ? (
                        <span className="rounded bg-foreground-muted/15 px-1.5 py-0.5 text-[10px] text-foreground-muted">
                          비활성
                        </span>
                      ) : null}
                    </div>
                    <div className="truncate text-xs text-foreground-muted">{u.email}</div>
                  </div>
                  <span
                    className={`shrink-0 opacity-0 transition-opacity group-hover:opacity-100 ${actionTone}`}
                  >
                    {actionLabel}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────── 5. 제외 사용자 ───────────────────────────

function ExclusionsSection({ role }: { role: RbacRole }) {
  const qc = useQueryClient();
  const exclusionsQuery = useQuery({
    queryKey: ['admin', 'roles', role.id, 'exclusions'],
    queryFn: () => api.adminRoleGetExclusions(role.id),
  });
  const removeMutation = useMutation({
    mutationFn: (userId: string) => api.adminUserRemoveRoleExclusion(userId, role.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'roles', role.id, 'exclusions'] });
      // effective skill 도 갱신 (UI 다른 곳)
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });

  const exclusions = exclusionsQuery.data?.exclusions ?? [];

  return (
    <Section
      title="제외된 사용자"
      description="이 그룹으로 부여된 권한을 명시적으로 차단한 사용자. 차단 추가는 사용자 상세 페이지에서."
    >
      {exclusionsQuery.isLoading ? (
        <div className="text-sm text-foreground-muted">불러오는 중…</div>
      ) : exclusions.length === 0 ? (
        <div className="rounded-md border border-border-muted bg-surface-muted px-3 py-2 text-sm text-foreground-muted">
          제외된 사용자가 없습니다.
        </div>
      ) : (
        <div className="rounded-md border border-border bg-surface p-2 space-y-1.5">
          {exclusions.map((ex) => (
            <div key={ex.userId} className="flex items-start justify-between gap-2 text-sm">
              <div className="min-w-0">
                <div className="font-medium">{ex.displayName}</div>
                <div className="text-xs text-foreground-muted">{ex.email}</div>
                {ex.reason ? (
                  <div className="mt-0.5 text-xs text-foreground-muted">사유: {ex.reason}</div>
                ) : null}
              </div>
              <Button
                type="button"
                variant="ghost"
                onClick={() => removeMutation.mutate(ex.userId)}
                disabled={removeMutation.isPending}
              >
                복구
              </Button>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

// ─────────────────────────── 공통 ───────────────────────────

function Section({
  title,
  description,
  action,
  children,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-md border border-border bg-surface p-4">
      <header className="mb-3 flex items-start justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold">{title}</h2>
          {description ? (
            <p className="mt-0.5 text-xs text-foreground-muted">{description}</p>
          ) : null}
        </div>
        {action}
      </header>
      {children}
    </section>
  );
}

function arrayEq(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const setA = new Set(a);
  return b.every((x) => setA.has(x));
}

// ─────────────────────────── 멤버 보기 모달 (3 section) ───────────────────────────

/**
 * 그룹의 실제 적용 상태 — 저장된 자동 부여 룰 매칭 + 수동 부여 + 제외 사용자.
 * 인라인 룰 미리보기 (편집 중 state) 와 달리 항상 DB 저장 상태 기준.
 * 탭으로 한 번에 1 section 만 렌더 → 키 입력 시 다른 section DOM 재생성 부담 없음.
 */
type MemberTab = 'auto' | 'manual' | 'exclusions';

function MembersOverviewModal({
  open,
  onClose,
  role,
}: {
  open: boolean;
  onClose: () => void;
  role: RbacRole;
}) {
  const isAllUsers = role.systemKind === 'all_users';
  const [draft, setDraft] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setSearch(draft.trim()), 200);
    return () => clearTimeout(t);
  }, [draft]);

  // all_users 그룹은 자동/수동 탭 의미 없으므로 exclusions 탭만.
  const [tab, setTab] = useState<MemberTab>(isAllUsers ? 'exclusions' : 'auto');

  // 저장된 룰 가져오기 (자동 탭에서만 사용).
  const grantsQuery = useQuery({
    queryKey: ['admin', 'roles', role.id, 'attribute-grants'],
    queryFn: () => api.adminRoleGetAttributeGrants(role.id),
    enabled: open && !isAllUsers,
  });
  const savedRules = grantsQuery.data?.rules ?? [];

  // 1) 자동 부여
  const autoQuery = useQuery({
    queryKey: ['admin', 'roles', role.id, 'preview-members', 'saved', { search }],
    queryFn: () =>
      api.adminRolePreviewMembers(role.id, {
        rules: savedRules,
        search: search || undefined,
      }),
    enabled: open && !isAllUsers && savedRules.length > 0,
  });

  // 2) 수동 부여
  const manualQuery = useQuery({
    queryKey: ['admin', 'roles', role.id, 'members-detail'],
    queryFn: () => api.adminRoleGetMembers(role.id),
    enabled: open && !isAllUsers,
  });

  // 3) 제외 사용자
  const exclusionsQuery = useQuery({
    queryKey: ['admin', 'roles', role.id, 'exclusions'],
    queryFn: () => api.adminRoleGetExclusions(role.id),
    enabled: open,
  });

  // 검색 필터 — draft 가 아닌 debounced search 에만 의존 (입력 중 재계산 안 함).
  const filteredManual = useMemo(() => {
    const data = manualQuery.data?.members ?? [];
    if (!search) return data;
    const s = search.toLowerCase();
    return data.filter(
      (u) => u.displayName.toLowerCase().includes(s) || u.email.toLowerCase().includes(s),
    );
  }, [manualQuery.data, search]);

  const filteredExclusions = useMemo(() => {
    const data = exclusionsQuery.data?.exclusions ?? [];
    if (!search) return data;
    const s = search.toLowerCase();
    return data.filter(
      (u) => u.displayName.toLowerCase().includes(s) || u.email.toLowerCase().includes(s),
    );
  }, [exclusionsQuery.data, search]);

  const autoTotal = autoQuery.data?.totalCount ?? 0;
  const autoMembers = autoQuery.data?.members ?? [];
  const manualTotal = manualQuery.data?.members.length ?? 0;
  const exclusionsTotal = exclusionsQuery.data?.exclusions.length ?? 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`'${role.name}' 멤버 보기`}
      description={
        isAllUsers
          ? '전체 사용자 그룹 — 모든 비-admin 사용자가 자동 멤버. 제외 사용자만 표시.'
          : '저장된 룰 기준의 자동 부여 + 수동 부여 + 제외 사용자.'
      }
      size="xl"
    >
      <div className="space-y-3">
        <Input
          placeholder="이메일 / 이름 검색"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          autoFocus
        />

        {/* 탭 헤더 */}
        <div className="flex gap-1 border-b border-border">
          {!isAllUsers ? (
            <>
              <TabButton
                active={tab === 'auto'}
                onClick={() => setTab('auto')}
                tone="success"
                label="자동 부여"
                count={autoTotal}
              />
              <TabButton
                active={tab === 'manual'}
                onClick={() => setTab('manual')}
                tone="info"
                label="수동 부여"
                count={manualTotal}
              />
            </>
          ) : null}
          <TabButton
            active={tab === 'exclusions'}
            onClick={() => setTab('exclusions')}
            tone="destructive"
            label="제외된 사용자"
            count={exclusionsTotal}
          />
        </div>

        {/* 탭 본문 — 한 번에 1개만 렌더 */}
        <div className="rounded-md border border-border">
          <div className="max-h-[70vh] min-h-[400px] overflow-y-auto p-1.5">
            {tab === 'auto' ? (
              <MemberListBody
                isLoading={grantsQuery.isLoading || autoQuery.isLoading}
                error={autoQuery.error}
                members={autoMembers}
                emptyHint={
                  savedRules.length === 0 ? '저장된 룰이 없습니다.' : '매칭되는 사용자 없음.'
                }
              />
            ) : tab === 'manual' ? (
              <MemberListBody
                isLoading={manualQuery.isLoading}
                error={manualQuery.error}
                members={filteredManual}
                emptyHint={search ? '검색 결과 없음.' : '수동 할당 멤버 없음.'}
              />
            ) : (
              <MemberListBody
                isLoading={exclusionsQuery.isLoading}
                error={exclusionsQuery.error}
                members={filteredExclusions.map((ex) => ({
                  id: ex.userId,
                  displayName: ex.displayName,
                  email: ex.email,
                  disabledAt: null,
                  reason: ex.reason,
                }))}
                emptyHint={search ? '검색 결과 없음.' : '제외된 사용자 없음.'}
              />
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}

interface MemberRow {
  id: string;
  displayName: string;
  email: string;
  disabledAt: string | null;
  reason?: string | null;
}

function TabButton({
  active,
  onClick,
  tone,
  label,
  count,
}: {
  active: boolean;
  onClick: () => void;
  tone: 'success' | 'info' | 'destructive';
  label: string;
  count: number;
}) {
  const toneClass =
    tone === 'success'
      ? 'border-success text-success'
      : tone === 'info'
        ? 'border-info text-info'
        : 'border-destructive text-destructive';
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        '-mb-px border-b-2 px-3 py-1.5 text-sm font-medium transition-colors',
        active ? toneClass : 'border-transparent text-foreground-muted hover:text-foreground',
      )}
    >
      {label}
      <span className="ml-1.5 text-xs text-foreground-muted">{count}</span>
    </button>
  );
}

const MemberListBody = memo(function MemberListBody({
  isLoading,
  error,
  members,
  emptyHint,
}: {
  isLoading: boolean;
  error: unknown;
  members: MemberRow[];
  emptyHint: string;
}) {
  if (isLoading) {
    return <div className="px-2 py-6 text-center text-sm text-foreground-muted">불러오는 중…</div>;
  }
  if (error) {
    return (
      <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
        {error instanceof ApiError ? error.message : '조회 실패'}
      </div>
    );
  }
  if (members.length === 0) {
    return <div className="px-2 py-6 text-center text-sm text-foreground-muted">{emptyHint}</div>;
  }
  return (
    <ul className="space-y-0.5">
      {members.map((m) => (
        <li
          key={m.id}
          className="flex items-start justify-between gap-2 rounded px-2 py-1 text-sm hover:bg-surface-muted"
        >
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="truncate font-medium">{m.displayName}</span>
              {m.disabledAt ? (
                <span className="rounded bg-foreground-muted/15 px-1.5 py-0.5 text-[10px] text-foreground-muted">
                  비활성
                </span>
              ) : null}
            </div>
            <div className="truncate text-xs text-foreground-muted">{m.email}</div>
            {m.reason ? (
              <div className="mt-0.5 text-[11px] text-foreground-muted">사유: {m.reason}</div>
            ) : null}
          </div>
        </li>
      ))}
    </ul>
  );
});
